import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useTheme } from '../../hooks/useTheme';

import type { NavRailItem, NavSection } from './config';
import { NAV_THEMES } from './nav-tokens';
import NavItemLink from './NavItemLink';
import { useFlyoutKeyboard } from './useFlyoutKeyboard';
import { useFlyoutPosition } from './useFlyoutPosition';

interface NavSectionFlyoutProps {
  id: string;
  section: NavSection;
  items: NavRailItem[];
  /** The row this hangs off — what it positions against. */
  anchor: HTMLElement;
  /** Move focus to the first page, because the keyboard opened it. */
  autoFocus: boolean;
  onClose: (restoreFocus?: boolean) => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  /** Hands the surface to the dismiss handler, so a press that lands inside
   *  the fly-out is not mistaken for a press outside it. */
  onSurface: (el: HTMLElement | null) => void;
}

/**
 * A section's pages, opened alongside the nav rather than pushed into it.
 *
 * It renders through a portal because the panel's two bands are scroll
 * containers and the app shell clips its overflow, so anything positioned
 * inside them would be cut off.
 *
 * Pages stay ordinary links — the rows a viewer can cmd-click today keep
 * working — so this is a disclosure, with the trigger's `aria-expanded` for
 * state, rather than a menu.
 */
export default function NavSectionFlyout({
  id,
  section,
  items,
  anchor,
  autoFocus,
  onClose,
  onPointerEnter,
  onPointerLeave,
  onSurface,
}: NavSectionFlyoutProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  // A callback ref rather than a plain one: the dismiss handler runs on
  // `pointerdown`, which happens BEFORE the click on a link inside the
  // fly-out. Without the surface registered, that press reads as a press
  // outside, the fly-out unmounts under the pointer, and the click never
  // reaches the link — every destination in it becomes unclickable.
  const attachSurface = useCallback(
    (el: HTMLDivElement | null) => {
      surfaceRef.current = el;
      onSurface(el);
    },
    [onSurface],
  );
  const pos = useFlyoutPosition(anchor, surfaceRef, items.length, onClose);
  const { isTopmost } = useFlyoutKeyboard(surfaceRef, autoFocus, onClose);

  return createPortal(
    <div
      id={id}
      ref={attachSurface}
      role="group"
      aria-label={section.label}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      // Tabbing past the last page is a way out of the fly-out, so it closes
      // rather than leaving an orphaned panel behind the focus ring. Gated on
      // the modal stack: a layer opening above takes focus too, and that is
      // not the viewer leaving.
      onBlur={(e) => {
        if (!isTopmost()) return;
        const next = e.relatedTarget as Node | null;
        // A null `relatedTarget` is the whole document losing focus — another
        // window, or the tab going to the background. The viewer has not left
        // the fly-out, and closing it would mean coming back to a nav that
        // quietly reset itself.
        if (next === null) return;
        if (!e.currentTarget.contains(next)) onClose();
      }}
      className="fixed z-[45] flex w-[212px] flex-col gap-0.5 overflow-y-auto rounded-xl p-2"
      style={{
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        maxHeight: 'calc(100vh - 24px)',
        background: t.bg,
        border: `1px solid ${t.border}`,
        boxShadow: '0 18px 44px -18px rgba(15, 25, 18, 0.45)',
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <div
        className="px-2 pb-1 pt-0.5 text-[10px] font-bold uppercase tracking-wider"
        style={{ color: t.muted }}
      >
        {section.label}
      </div>
      {items.map((item) => (
        <NavItemLink key={item.id} item={item} variant="drawer" onNavigate={() => onClose()} />
      ))}
    </div>,
    document.body,
  );
}
